import type {
  Journey,
  JourneyRenderFlags,
  JourneySession,
  next
} from '@epilot/journey-logic-commons'
import {
  initServices as initLogicCommonsServices,
  JourneyFeatureFlags as FeatureFlags
} from '@epilot/journey-logic-commons'
import { JOURNEY_EMBED_MODE } from '@epilot/journey-logic-commons/src/types/next'
import type { PropsWithChildren } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { v4 as uuidV4 } from 'uuid'

import {
  AuthenticatedUserProvider,
  ConfigProvider,
  JourneyContextProvider,
  useHistoryStack,
  useUserProgress
} from './blocks-renderers'
import { LiveAnnouncer } from './components/accessibility/LiveAnnouncer'
import { JourneyLauncherTabs } from './components/JourneyLauncher/JourneyLauncherTabs'
import { JourneyLauncherTabsThemeProvider } from './components/JourneyLauncher/JourneyLauncherTabsThemeProvider'
import { JourneyPage } from './components/JourneyPage'
import { publishJourneyLoadedMessage } from './components/JourneyPage/embedJourneyPublishers'
import { JourneyPageNext } from './components/JourneyPage/JourneyPageNext'
import { SessionSelectorDialog } from './components/SessionSelectorDialog'
import { SpinnerPage } from './components/SpinnerPage'
import { useAppEvents } from './hooks/useAppEvents'
import { useAppStoreSelector } from './hooks/useAppStoreSelector'
import { useFlag } from './hooks/useFlags'
import { useGetJourney } from './hooks/useGetJourney'
import { useMessageHandler } from './hooks/useMessageHandler'
import { usePrefetchCatalogQueries } from './hooks/usePrefetchCatalogQueries'
import { selectAppParamsOrThrows } from './store/selectors/runtime'
import {
  ADDRESS_SUGGESTIONS_API,
  JOURNEYS_PRICING_API,
  rendererConfig
} from './utils/config'
import { getInitialHistoryStack } from './utils/getInitialHistoryStack'
import { getParamsByContextValues } from './utils/getParamsByContextValues'
import { parseUuidOrValueParams } from './utils/parse'
import { scrollToThePageTop } from './utils/scrollToTop'
import { TRACE_KEYS, getTraceId } from './utils/trace'

const sessionIdGetter = () => getTraceId(TRACE_KEYS.JOURNEY_SESSION_ID)

/**
 * If it's rendering a launcher journey, it will wrap the children in the tabs,
 * otherwise it will just return the children
 */
const AppWrapper = ({
  launcherJourney,
  isFocused,
  children
}: PropsWithChildren<{
  isFocused: boolean
  launcherJourney?: next.LauncherJourney
}>) => {
  if (!launcherJourney) {
    return <>{children}</>
  }

  return (
    <JourneyLauncherTabsThemeProvider isFocusedOnJourney={isFocused}>
      <JourneyLauncherTabs
        isFocusedOnJourney={isFocused}
        showPaper={launcherJourney.settings.placementOnCard}
      >
        {children}
      </JourneyLauncherTabs>
    </JourneyLauncherTabsThemeProvider>
  )
}

export const App = ({
  journeyIdOverride,
  parentJourney = null,
  launcherJourney
}: {
  journeyIdOverride?: string | null
  parentJourney?: Journey | null
  launcherJourney?: next.LauncherJourney
}) => {
  const {
    debug,
    preview,
    topBar: topBarParam,
    mode: modeParam,
    isEmbedded,
    dataInjectionOptions: dataInjectionOptionsParam
  } = useAppStoreSelector(selectAppParamsOrThrows)

  const {
    journey,
    stepIndex: jbStepIndex,
    isLoading,
    initialState,
    isLinearJourney,
    journeyId,
    contextData: previewContextData,
    setJourney
  } = useGetJourney({ journeyIdOverride })

  const {
    mode: modeMessage,
    topBar,
    scrollToTop,
    closeButton,
    contextData,
    queryParams,
    dataInjectionOptions,
    isFullScreenEntered,
    initialMessageEventReceived
  } = useMessageHandler(journeyId || undefined) || {}

  const contextParameter = useMemo(() => {
    return parseUuidOrValueParams({
      ...getParamsByContextValues(queryParams || {}, journey?.contextSchema),
      ...(contextData || previewContextData)
    })
  }, [contextData, journey?.contextSchema, previewContextData, queryParams])

  const {
    isUserProgressEnabled,
    currentSessionId,
    setCurrentSessionId,
    getExistingSessions,
    saveProgress,
    clearCurrentUserSession
  } = useUserProgress({
    journey,
    paramContext: contextParameter
  })

  const isLogicsEngineEnabled = useFlag(FeatureFlags.LOGICS_ENGINE)

  useEffect(() => {
    initLogicCommonsServices({
      pricingServiceUrl: JOURNEYS_PRICING_API,
      addressSuggestionsServiceUrl: ADDRESS_SUGGESTIONS_API,
      orgId: journey?.organizationId || '',
      publicToken: journey?.settings?.publicToken || ''
    })
  }, [journey?.organizationId, journey?.settings?.publicToken])

  const mode = modeMessage || modeParam

  const {
    initialState: injectionInitialState,
    initialStepIndex: injectionStepIndex,
    blocksDisplaySettings
  } = dataInjectionOptionsParam || dataInjectionOptions || {}

  const initialStepIndex = injectionStepIndex ?? jbStepIndex ?? 0

  const [journeyInitialState, setJourneyInitialState] = useState(
    /**
     * This clone is to avoid issues where the initial state gets mutated later in the journey
     * which will cause the journey to remount due to forceRemountAfterInjection used as react key in JourneyContextProvider.
     * TODO: Let's clarify if this remount forcing is really needed or not.
     */
    structuredClone(injectionInitialState || initialState)
  )

  const history = useHistoryStack({
    initialValues: {
      stepName: journey?.steps?.[initialStepIndex]?.name || '',
      stepIndex: initialStepIndex
    },
    scrollToThePageTop,
    journey,
    // when journey is using injection, it should not allow navigating back to a skip step
    initialHistoryStack: getInitialHistoryStack(
      journey?.steps,
      injectionStepIndex
    ),
    options: {
      scrollIntoView:
        typeof scrollToTop === 'undefined' || scrollToTop === true,
      forceInitialize: preview,
      isTrackingDisabled:
        mode === JOURNEY_EMBED_MODE.FULL_SCREEN && !isFullScreenEntered,
      isUserProgressEnabled,
      saveProgress
    }
  })

  useAppEvents({ history })

  const [existingSessions, setExistingSessions] = useState<JourneySession[]>([])

  const onRestoreSession = (sessionId: string) => {
    setCurrentSessionId(sessionId, rendererConfig.JOURNEYS_API_BASE_URL)
    const session = existingSessions.find((s) => s.id === sessionId)

    if (session && journey) {
      setJourneyInitialState(session.stepsState)
      history.initSession(session, journey.steps)
    }
  }

  useEffect(() => {
    if (
      isUserProgressEnabled &&
      journey?.journeyId &&
      !preview &&
      !currentSessionId
    ) {
      getExistingSessions(rendererConfig.JOURNEYS_API_BASE_URL).then(
        (sessions) => {
          if (
            Array.isArray(sessions) &&
            sessions.length > 0 &&
            initialStepIndex === 0
          ) {
            setExistingSessions(sessions)
          } else {
            setCurrentSessionId(uuidV4(), rendererConfig.JOURNEYS_API_BASE_URL)
            setExistingSessions([])
          }
        }
      )
    }
  }, [
    getExistingSessions,
    preview,
    currentSessionId,
    initialStepIndex,
    journey,
    isUserProgressEnabled,
    setCurrentSessionId
  ])

  usePrefetchCatalogQueries({
    journey,
    isPreview: preview,
    pricingApiUrl: rendererConfig.PRICING_API_URL
  })

  const journeyFlags: JourneyRenderFlags = {
    debug,
    isLinear: isLinearJourney,
    isPreview: preview,
    showCloseButton: closeButton,
    showTopBar:
      typeof topBar === 'boolean'
        ? topBar
        : typeof topBarParam === 'boolean'
          ? topBarParam
          : true,
    mode: mode,
    scrollToTop: scrollToTop || false
  }

  const contextValues = useMemo(
    () => ({
      ...contextParameter,
      embedded_in: document.referrer,
      journey_url_used: document.location.href
    }),
    [contextParameter]
  )

  const forceRemountAfterInjection =
    injectionInitialState || currentSessionId
      ? JSON.stringify(injectionInitialState)
      : 'normalcontext'

  /**
   * Publish event when journey loads.
   * Event gets handled by journey-embed, which emits back
   * an event with `type` EPILOT/INIT as confirmation
   */
  useEffect(() => {
    if (journey) {
      publishJourneyLoadedMessage({
        journeyName: journey.name,
        journeyId: journey.journeyId
      })
    }
  }, [journey])

  const showSessionSelectorDialog =
    !currentSessionId &&
    !preview &&
    existingSessions.length > 0 &&
    history.currentIndex === 0

  // show spinner while loading or while initializing in preview mode
  if (isLoading || !journey) return <SpinnerPage />

  const isFocused = parentJourney ? !!history.currentIndex : true
  const JourneyPageComponent = isLogicsEngineEnabled
    ? JourneyPageNext
    : JourneyPage

  return (
    <AppWrapper isFocused={isFocused} launcherJourney={launcherJourney}>
      <ConfigProvider config={rendererConfig}>
        <JourneyContextProvider
          blocksDisplaySettings={blocksDisplaySettings}
          contextValues={contextValues}
          history={history}
          initialState={journeyInitialState}
          isPreview={preview}
          journey={journey}
          key={forceRemountAfterInjection}
          sessionIdGetter={sessionIdGetter}
          shouldCheckContextValues={
            isEmbedded ? !!initialMessageEventReceived : true
          }
        >
          <AuthenticatedUserProvider>
            <LiveAnnouncer>
              <JourneyPageComponent
                clearCurrentUserSession={clearCurrentUserSession}
                flags={journeyFlags}
                history={history}
                initialStepValues={initialState}
                isFocusedOnJourney={isFocused}
                journey={journey}
                key={journey.journeyId}
                launcherJourney={parentJourney}
                onJourneyChange={setJourney}
              />
            </LiveAnnouncer>
            {isUserProgressEnabled && (
              <SessionSelectorDialog
                availableSessions={existingSessions}
                onClose={() => {
                  setCurrentSessionId(
                    uuidV4(),
                    rendererConfig.JOURNEYS_API_BASE_URL
                  )
                  setExistingSessions([])
                }}
                onSessionSelected={onRestoreSession}
                open={showSessionSelectorDialog}
              />
            )}
          </AuthenticatedUserProvider>
        </JourneyContextProvider>
      </ConfigProvider>
    </AppWrapper>
  )
}

App.displayName = 'App'
